<template>
  <div>
    <AppDeliveryTargetSetting
      v-if="!isRailwayMenu"
      :menu-setting="form.campaign.menuSetting"
    />
    <AdvancedSetting
      v-if="isVisitorSelected"
      button-text="詳細設定"
      class="mb-3"
    >
      <AppDeliveryTargetAdvancedSetting :campaign="form.campaign" />
    </AdvancedSetting>
    <v-layout>
      <v-switch
        v-if="isCustomGeoMenu && isVisitorSelected"
        v-model="menuSetting.useS2"
        label="精度の低い位置情報を利用する"
      />
      <v-switch
        ref="switchRadiusAutomation"
        v-else-if="!isCustomGeoMenu"
        v-model="enableRadiusAutomation"
        label="半径の自動最適化モード"
      />
    </v-layout>
    <div v-if="!isCustomGeoMenu && !enableRadiusAutomation" class="indentBlock">
      <v-layout>
        <v-layout>
          <v-switch
            v-if="isRailwayMenu"
            v-model="menuSetting.useS2"
            :disabled="!menuSetting.availableUseS2"
            label="精度の低い位置情報を利用する（半径1,000m未満に設定された場合は利用されません）"
          />
          <v-switch
            v-else
            v-model="menuSetting.useLowQualityLocation"
            :disabled="!menuSetting.availableUseS2"
            label="精度の低い位置情報を利用する（半径1,000m未満に設定された場合は利用されません）"
          />
        </v-layout>
      </v-layout>
      <v-layout column>
        <v-radio-group v-model="enableBulkSetting">
          <v-layout>
            <v-radio :value="true" label="一括半径指定" />
            <v-text-field
              v-model.number="radius"
              :disabled="!enableBulkSetting"
              :rules="[
                rules.required,
                rules.overHundred,
                rules.overHundredAndLessTenThousand,
                rules.integer,
              ]"
              :class="{ disabled: !enableBulkSetting }"
              @keydown.arrow-down.prevent
              @keydown.arrow-up.prevent
              @wheel.prevent
              type="number"
            >
              <template v-slot:append-outer>
                <span>m</span>
              </template>
            </v-text-field>
          </v-layout>
          <v-layout column>
            <v-radio :value="false" :label="`${label}ごとに半径を設定する`" />
            <div class="indentBlock">
              <v-text-field
                v-for="item in targetSettingRadius"
                :key="item.id"
                v-model.number="item.radius"
                :rules="[
                  rules.required,
                  rules.overHundred,
                  rules.overHundredAndLessTenThousand,
                  rules.integer,
                ]"
                :disabled="enableBulkSetting"
                :class="{ disabled: enableBulkSetting }"
                @keydown.arrow-down.prevent
                @keydown.arrow-up.prevent
                @wheel.prevent
                @input="changeRadius(item)"
                style="min-width=240px;"
                type="number"
              >
                <template v-slot:prepend>
                  <span class="text-no-wrap pt-2">{{ itemName(item) }}</span>
                </template>
                <template v-slot:append-outer>
                  <span>m</span>
                </template>
              </v-text-field>
            </div>
          </v-layout>
        </v-radio-group>
      </v-layout>
    </div>
  </div>
</template>

<script>
import AdvancedSetting from '../../../Shared/CrudUI/AdvancedSetting.vue'
import { MenuTargets, TargetVisitorConsts } from '@/models/CampaignOperation'
import AppDeliveryTargetSetting from '@/components/App/AppDeliveryTargetSetting'
import AppDeliveryTargetAdvancedSetting from '@/components/App/AppDeliveryTargetAdvancedSetting'
import { getMenuSetting } from './Service/getMenuSettingService'
import { getTargetSettingRadius } from './Service/getTargetSettingRadiusService'
import { getOrderDescription } from './Service/getOrderDescriptionService'

const ONE_DAY = 60 * 24

export default {
  name: 'CampaignModalPageTarget',
  components: {
    AppDeliveryTargetAdvancedSetting,
    AppDeliveryTargetSetting,
    AdvancedSetting,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      rules: {
        required: value =>
          Boolean(value !== undefined && value !== '') || '入力して下さい',
        overHundred: value =>
          Boolean(this.isRailwayMenu || Number(value) >= 100) ||
          '100以上の数値を入力して下さい',
        overHundredAndLessTenThousand: value =>
          Boolean(
            !this.isRailwayMenu ||
              (Number(value) >= 100 && Number(value) <= 10000),
          ) || '100以上10,000以下の数値を入力して下さい',
        integer: value =>
          Number.isInteger(Number(value)) || '整数で入力して下さい',
      },
    }
  },
  computed: {
    isCustomGeoMenu() {
      return this.form.campaign.hasCustomGeoMenuSetting
    },
    isRailwayMenu() {
      return this.form.campaign.hasRailwayMenuSetting
    },
    menuSetting() {
      return getOrderDescription(this.form.campaign)
    },
    isVisitorSelected() {
      if (this.isRailwayMenu) return true
      return this.menuSetting.target.includes(MenuTargets.VISITOR)
    },
    minutesAgo() {
      return this.menuSetting.minutesAgo
    },
    label() {
      if (this.isRailwayMenu) {
        return '路線'
      } else {
        return '地点グループ'
      }
    },
    targetSettingRadius() {
      return getTargetSettingRadius(this.form.campaign)
    },
    enableBulkSetting: {
      get() {
        return this.menuSetting.enableBulkSetting
      },
      set(value) {
        this.menuSetting.setEnableBulkSetting(value)
      },
    },
    minutesAgoIsLowerThanADay: {
      get() {
        return this.minutesAgo < ONE_DAY
      },
      set(value) {
        if (value) {
          this.menuSetting.minutesAgo = TargetVisitorConsts.MAX_HOURS * 60
        } else {
          this.menuSetting.minutesAgo = ONE_DAY
        }
      },
    },
    minutesAgoAsDays: {
      get() {
        return Math.floor(this.minutesAgo / 60 / 24)
      },
      set(newDays) {
        if (newDays === 0) {
          this.menuSetting.minutesAgo = 0
        } else {
          const days = Math.min(
            Number(newDays) || 1,
            TargetVisitorConsts.MAX_DAYS,
          )
          this.menuSetting.minutesAgo = days * 60 * 24
        }
      },
    },
    minutesAgoAsHours: {
      get() {
        return Math.floor(this.minutesAgo / 60)
      },
      set(newHours) {
        if (newHours < 1) {
          this.menuSetting.minutesAgo = newHours * 60
          return
        }
        const hours = Math.min(
          Number(newHours) || 1,
          TargetVisitorConsts.MAX_HOURS,
        )
        this.menuSetting.minutesAgo = hours * 60
      },
    },
    enableRadiusAutomation: {
      get() {
        return this.menuSetting.enableRadiusAutomation
      },
      async set(flag) {
        const isFromSimulation = this.form.campaign.isFromSimulation
        if (!flag) {
          const body =
            '※半径の自動最適化モードを有効にしておくことを強く推奨します'
          const result = await this.$confirm(
            '半径の自動最適化が効かなくなりますがよろしいですか？',
            { body },
          )
          if (!result) {
            this.menuSetting.setEnableRadiusAutomation(true, isFromSimulation)
            /* NOTE: vuetify v-switch の内部値を変えるハック */
            this.$refs.switchRadiusAutomation.internalValue = true
            return
          }
        }
        this.menuSetting.setEnableRadiusAutomation(flag, isFromSimulation)
      },
    },
    radius: {
      get() {
        return this.menuSetting.radius
      },
      set(value) {
        const radius = value === '' ? undefined : value
        this.menuSetting.setRadiusToAllRadiusTarget(radius)
        this.menuSetting.radius = radius
      },
    },
    errorMessage() {
      const errorMessages = []
      if (getMenuSetting(this.form.campaign).emptyVisitCountViolation)
        errorMessages.push(
          '過去訪問回数が入力されていません。\n入力してください。',
        )
      if (getMenuSetting(this.form.campaign).zeroVisitCountViolation)
        errorMessages.push('過去訪問回数の数値は1以上を入力してください。')
      if (getMenuSetting(this.form.campaign).emptyRadiusViolation)
        errorMessages.push('半径が入力されていません。\n入力してください。')
      if (getMenuSetting(this.form.campaign).lessRadiusViolation) {
        if (this.isRailwayMenu) {
          errorMessages.push(
            '半径の数値は100以上10,000以下を入力してください。',
          )
        } else {
          errorMessages.push('半径の数値は100以上を入力してください。')
        }
      }
      if (getMenuSetting(this.form.campaign).emptyMinutesAgoViolation)
        errorMessages.push('過去訪問期間を入力してください。')
      if (getMenuSetting(this.form.campaign).minusMinutesAgoViolation)
        errorMessages.push('過去訪問期間は0時間以上を指定してください。')
      if (getMenuSetting(this.form.campaign).noTargetViolation)
        errorMessages.push(
          '訪問者もしくは居住者のいずれかの設定は必須です。\n1つ以上チェックを入れてください。',
        )
      return errorMessages.length === 0 ? '' : errorMessages
    },
  },
  watch: {
    minutesAgo(newValue) {
      const value = Number(newValue) || TargetVisitorConsts.MIN_MINUTES
      if (TargetVisitorConsts.MIN_MINUTES > value) {
        this.menuSetting.minutesAgo = TargetVisitorConsts.MIN_MINUTES
      } else if (value > TargetVisitorConsts.MAX_MINUTES) {
        this.menuSetting.minutesAgo = TargetVisitorConsts.MAX_MINUTES
      }

      this.form.campaign.menuSetting.coerceUseS2State()
    },
    'menuSetting.target'() {
      this.form.campaign.menuSetting.coerceUseS2State()
    },
  },
  created() {
    this.form.campaign.menuSetting.coerceUseS2State()
  },
  methods: {
    changeRadius(line) {
      if (this.isRailwayMenu) {
        this.menuSetting.setRadius(line)
      }
    },
    async onSpinDays(e) {
      const days = Number(e.target.value)
      if (days >= 365 && e.key === 'ArrowUp') e.preventDefault()
      if (days <= 1 && e.key === 'ArrowDown') {
        e.preventDefault()
        this.minutesAgoAsHours = TargetVisitorConsts.MAX_HOURS
        await this.$nextTick()
        this.$refs.minutesAgoAsHours.focus()
      }
    },
    async onSpinHours(e) {
      const hours = Number(e.target.value)
      if (hours >= TargetVisitorConsts.MAX_HOURS && e.key === 'ArrowUp') {
        e.preventDefault()
        this.minutesAgoAsDays = TargetVisitorConsts.MIN_DAYS
        await this.$nextTick()
        this.$refs.minutesAgoAsDays.focus()
      }
      if (hours <= TargetVisitorConsts.MIN_HOURS && e.key === 'ArrowDown')
        e.preventDefault()
    },
    itemName(item) {
      if (this.isRailwayMenu) {
        return item.lineName
      }
      const locationGroup = this.menuSetting.findLocationGroup(
        item.locationGroupId,
      )
      return locationGroup == null ? '' : locationGroup.name
    },
  },
}
</script>

<style scoped lang="scss">
.textAppended {
  display: inline-block;
  padding-top: 10px;
}

.indentBlock {
  padding-left: 30px;
}

.withoutSpinButton {
  ::v-deep input[type='number'] {
    -moz-appearance: textfield;
  }

  ::v-deep input[type='number']::-webkit-outer-spin-button,
  ::v-deep input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
}

.disabled {
  ::v-deep .v-input__slot {
    background-color: rgba(0, 0, 0, 0.05);
  }
}
.mention {
  color: #aaa;
}
</style>
